import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { api } from '../../../shared/api.js';
import { useReceptionForm } from './useReceptionForm.js';

/**
 * تغطية الاشتراك الشهري لأطفال نموذج الاستقبال.
 *
 * يُستعلم فقط عن الأطفال المسجَّلين سابقاً (الذين لهم childId): الطفل الجديد
 * لا يمكن أن يكون مشتركاً، فلا طلب له.
 *
 * يعيد:
 *   byChildId: Map(childId -> نتيجة التغطية) للمغطّين فقط
 *   isCovered(child): هل سطر الطفل هذا مغطّى
 */
export function useSubscriptionCoverage() {
  const children = useReceptionForm((s) => s.children);

  const ids = useMemo(
    () => [...new Set(children.filter((c) => c.childId).map((c) => String(c.childId)))],
    [children]
  );

  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['subscriptions', 'coverage', id],
      queryFn: async () => {
        const { data } = await api.get(`/subscriptions/child/${id}`);
        return data;
      },
      staleTime: 15_000,
      // تعذُّر القراءة لا يجعل الطفل «مغطّى»: الخادم هو من يقرّر عند فتح
      // الجلسة، والواجهة تعرض التسعير العادي في هذه الحالة.
      retry: 0
    }))
  });

  // خريطة صغيرة (عدد أطفال العائلة) تُبنى كل رسم — أرخص من مَوازنة التبعيات.
  const byChildId = new Map();
  ids.forEach((id, i) => {
    const r = results[i];
    if (r && r.data && r.data.covered) byChildId.set(id, r.data);
  });

  const isCovered = (child) => !!(child && child.childId && byChildId.has(String(child.childId)));

  return {
    byChildId,
    isCovered,
    coveredCount: children.filter(isCovered).length,
    anyCovered: children.some(isCovered),
    loading: results.some((r) => r.isLoading)
  };
}
